import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PersonasPage.css';
import AdUnit from './AdUnit';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const AdSensePlaceholder = ({ position }) => (
  <div className="adsense-placeholder">
    <div className="ad-content">
      <span className="ad-label"></span>
      <p className="ad-text">{/*AdSense Ad #position*/}
        <AdUnit adSlot="1700647405" /> {/* Use your actual ad slot ID */}
      </p>
    </div>
  </div>
);

const PersonasPage = ({ user, token, onUpdate, onUpgradeClick }) => {
  const navigate = useNavigate(); 

  const [personas, setPersonas] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [newPersona, setNewPersona] = useState({
    name: '',
    dateOfBirth: '',
    ethnicity: '',
    gender: '',
    location: ''
  });

  const isFree = user.tier === 'free';
  const personaLimits = {
    free: 1,
    paid: 3,
    premium: 5
  };
  const maxPersonas = personaLimits[user.tier];
  const canAddPersona = personas.length < maxPersonas;

  useEffect(() => {
    fetchPersonas();
  }, []);

  const fetchPersonas = async () => {
    try {
      const response = await fetch(`${API_URL}/personas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPersonas(data);
      }
    } catch (err) {
      console.error('Error fetching personas:', err);
    }
  };

  const handleAddPersona = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!canAddPersona) {
      setError(`You've reached the limit of ${maxPersonas} persona(s) for your tier`);
      onUpgradeClick();
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/personas`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPersona)
      });

      if (response.ok) {
        setMessage('Persona added successfully! ✨');
        setShowAddModal(false);
        setNewPersona({
          name: '',
          dateOfBirth: '',
          ethnicity: '',
          gender: '',
          location: ''
        });
        fetchPersonas();
        onUpdate();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to add persona');
        if (data.error.includes('limit')) {
          //navigate('/upgrade'); // Use this instead of onUpgradeClick()
          onUpgradeClick();
        }
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePersona = async (personaId) => {
    if (!window.confirm('Are you sure you want to delete this persona?')) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/personas/${personaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setMessage('Persona deleted successfully !');
//        fetchEventSequences(selectedPersona);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError('Failed to delete event');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }finally {
      setLoading(false);
      
    }
    window.location.reload();
  };

  return (
    <div className="personas-page">
      <div className="cosmic-bg"></div>
      
      <div className="page-header">
        <h1 className="page-title">👥 Personas</h1>
        <p className="page-subtitle">Manage multiple profiles for personalized predictions</p>
      </div>

      <div className="personas-content">
        <div className="personas-main">
          {/* Personas Limit Info */}
          <div className="card limit-card">
            <div className="limit-info">
              <div className="limit-text">
                <span className="limit-icon">👤</span>
                <span>
                  <strong>{personas.length}</strong> of <strong>{maxPersonas}</strong> personas used
                </span>
              </div>
              {!canAddPersona && (
                <button className="upgrade-link" onClick={onUpgradeClick}>
                  Upgrade for more
                </button>
              )}
            </div>
            <div className="limit-bar">
              <div 
                className="limit-progress" 
                style={{ width: `${(personas.length / maxPersonas) * 100}%` }}
              ></div>
            </div>
          </div>

          {message && (
            <div className="success-message">
              <span className="success-icon">✓</span>
              {message}
            </div>
          )}

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {/* Personas Grid */}
          <div className="personas-grid">
            {/* Add New Persona Card */}
            <div 
              className={`persona-card add-card ${!canAddPersona ? 'locked' : ''}`}
              onClick={() => canAddPersona ? setShowAddModal(true) : onUpgradeClick()}
            >
              <div className="add-icon">
                {canAddPersona ? '➕' : '🔒'}
              </div>
              <h3 className="add-title">
                {canAddPersona ? 'Add New Persona' : 'Upgrade to Add More'}
              </h3>
              <p className="add-subtitle">
                {canAddPersona 
                  ? 'Create a new profile for predictions' 
                  : `Unlock ${personaLimits.premium} personas`}
              </p>
            </div>

            {/* Existing Personas */}
            {personas.map(persona => (
              <div key={persona.id} className="persona-card">
                <div className="persona-header">
                  <div className="persona-avatar">
                    {persona.name.charAt(0).toUpperCase()}
                  </div>
                  <button 
                    className="delete-btn"
                    onClick={() => handleDeletePersona(persona.id)}
                    title="Delete persona"
                  >
                    🗑️
                  </button>
                </div>
                <h3 className="persona-name">{persona.name}</h3>
                <div className="persona-details">
                  {persona.dateOfBirth && (
                    <div className="detail-item">
                      <span className="detail-icon">🎂</span>
                      <span>{new Date(persona.dateOfBirth).toLocaleDateString()}</span>
                    </div>
                  )}
                  {persona.gender && (
                    <div className="detail-item">
                      <span className="detail-icon">⚧️</span>
                      <span>{persona.gender}</span>
                    </div>
                  )}
                  {persona.location && (
                    <div className="detail-item">
                      <span className="detail-icon">📍</span>
                      <span>{persona.location}</span>
                    </div>
                  )}
                </div>
                <div className="persona-footer">
                  <span className="sequences-count">
                    📝 {persona.sequences?.length || 0} sequences
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* AdSense Placeholder 1 (Free tier) */}
          {isFree && <AdSensePlaceholder position={1} />}
        </div>

        {/* Sidebar with AdSense */}
        {isFree && (
          <div className="personas-sidebar">
            <AdSensePlaceholder position={2} />
          </div>
        )}
      </div>

      {/* Add Persona Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">✨ Create New Persona</h2>
              <button 
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddPersona} className="persona-form">
              <div className="input-group">
                <label className="input-label">
                  <span className="label-icon">👤</span>
                  Name *
                </label>
                <input
                  type="text"
                  className="persona-input"
                  value={newPersona.name}
                  maxlength="12"
                  onChange={(e) => setNewPersona({...newPersona, name: e.target.value.replace('=','@').replace('*','#').replace('!','1').replace('$','S')})}
                  placeholder="e.g., My Child, Partner, etc."
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">
                  <span className="label-icon">🎂</span>
                  Date of Birth
                </label>
                {/*<input
                  type="date"
                  className="persona-input"
                  value={newPersona.dateOfBirth}
                  onChange={(e) => setNewPersona({...newPersona, dateOfBirth: e.target.value})}
                /><br/>*/}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <select
                      className="event-input"
                      value={newPersona.dateOfBirth.split('-')[0] || ''}
                      onChange={(e) => {
                        const year = e.target.value;
                        const month = newPersona.dateOfBirth.split('-')[1] || '01';
                        const day = newPersona.dateOfBirth.split('-')[2] || '01';
                        setNewPersona({...newPersona, dateOfBirth:`${year}-${month}-${day}`});
                      }}
                      required
                      style={{ flex: 1 }}
                    >
                      <option value="">Year</option>
                      {Array.from({ length: 2025 - 1960 + 1 }, (_, i) => 2025 - 18 - i).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>

                    <select
                      className="event-input"
                      value={newPersona.dateOfBirth.split('-')[1] || ''}
                      onChange={(e) => {
                        const month = e.target.value.padStart(2, '0');
                        const year = newPersona.dateOfBirth.split('-')[0] || new Date().getFullYear();
                        const day = newPersona.dateOfBirth.split('-')[2] || '01';
                        setNewPersona({...newPersona, dateOfBirth:`${year}-${month}-${day}`});
                      }}
                      required
                      style={{ flex: 1 }}
                    >
                      <option value="">Month</option>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                        <option key={month} value={month.toString().padStart(2, '0')}>
                          {month}
                        </option>
                      ))}
                    </select>

                    <select
                      className="event-input"
                      value={newPersona.dateOfBirth.split('-')[2] || ''}
                      onChange={(e) => {
                        const day = e.target.value.padStart(2, '0');
                        const year = newPersona.dateOfBirth.split('-')[0] || new Date().getFullYear();
                        const month = newPersona.dateOfBirth.split('-')[1] || '01';
                        setNewPersona({...newPersona, dateOfBirth:`${year}-${month}-${day}`});
                      }}
                      required
                      style={{ flex: 1 }}
                    >
                      <option value="">Day</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day.toString().padStart(2, '0')}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>


              </div>

              <div className="input-group">
                <label className="input-label">
                  <span className="label-icon">⚧️</span>
                  Gender
                </label>
                <select
                  className="persona-select"
                  value={newPersona.gender}
                  onChange={(e) => setNewPersona({...newPersona, gender: e.target.value})}
                >
                  <option value="">Prefer not to say</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="input-group">
                <label className="input-label">
                  <span className="label-icon">🌍</span>
                  Ethnicity
                </label>

                <select
                  className="persona-select"
                  value={newPersona.ethnicity}
                  onChange={(e) => setNewPersona({...newPersona, ethnicity: e.target.value})}
                >
                  <option value="globetrotter">I belong to the world</option>
                  <option value="white caucasian">White Caucasian</option>
                  <option value="American">White American</option>
                  <option value="African American">African American</option>
                  <option value="Hispanic">Hispanic</option>
                  <option value="European">European</option>
                  <option value="African">African</option>
                  <option value="Middle-Eastern">Middle-Eastern</option>
                  <option value="Central-Asian">Central-Asian</option>
                  <option value="Indian">Indian Subcontinent</option>
                  <option value="Southeast Asian">South East Asian</option>
                  <option value="East Asian">East Asian/pacific</option>
                </select>
              </div>

              <div className="input-group">
                <label className="input-label">
                  <span className="label-icon">📍</span>
                  Location
                </label>

                <select
                    className="persona-select"
                    value={newPersona.location}
                    onChange={(e) => setNewPersona({...newPersona, location: e.target.value})}
                  >
                    <option value="North America">North America</option>
                    <option value="Latin America">Latin America</option>
                    <option value="Europe">Europe</option>
                    <option value="Africa">Africa</option>
                    <option value="Asia">Asia</option>
                    <option value="Australia">Australia</option>
                  </select>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-small"></span>
                      <span>Adding...</span>
                    </>
                  ) : (
                    'Add Persona'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonasPage;